import { useId, type ReactNode } from "react";
import { Spinner } from "../components/Spinner";

/* An action tile: a button whose label is an action and one sentence on what
 * it does, for a place where a person chooses among a few actions and each
 * needs that sentence to be chosen well. docs/design-standards.md, Shape
 * follows the job, and docs/style-guide.md, Action tiles.
 *
 * The title is the action, verb first, in the words a person would say. The
 * sentence is what happens or when to use it, at most two lines at the
 * tile's width. The icon tells the tiles in a row apart at a glance and is
 * hidden from a screen reader, which reads the title as the name and the
 * sentence as the description.
 *
 * A tile shows its own action's state in its sentence, so nothing appears
 * somewhere else on the page (James, 2026-10-09): while the action runs, the
 * spinner and what is happening, and when it fails, the reason in the
 * refusal color. A file the action makes downloads by itself when it is
 * ready, so a tile never needs a link inside it, which a button cannot hold.
 *
 * A setting that is on or off is a switch tile (James, 2026-10-09): pass
 * `on`, and the tile shows a switch reading On or Off at its top right, is a
 * switch to a screen reader, and its title names the setting while its
 * sentence says what it does in that state. A separate switch beside the
 * row read as a different kind of control from the tiles. */
export function ActionTile({ title, says, icon, onClick, disabled, busy, tone, on, kind = "plain" }: {
  title: string;
  says: ReactNode;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** The action is running. The icon becomes the spinner and the tile waits. */
  busy?: boolean;
  /** refusal when the sentence is the reason the action failed. */
  tone?: "refusal" | undefined;
  /** For a switch tile: whether the setting is on. */
  on?: boolean | undefined;
  /** orange for the one tile that moves the work forward, as the orange
   *  button does. At most one in a row, and none when the page header
   *  already shows the next step. */
  kind?: "plain" | "orange";
}) {
  const id = useId();
  return (
    <button
      type="button"
      className={`ui-tile ui-tile-${kind}${on === undefined ? "" : on ? " ui-tile-on" : " ui-tile-off"}`}
      role={on === undefined ? undefined : "switch"}
      aria-checked={on === undefined ? undefined : on}
      onClick={onClick}
      disabled={Boolean(disabled || busy)}
      aria-busy={busy ? true : undefined}
      aria-labelledby={`${id}-t`}
      aria-describedby={`${id}-s`}
    >
      {icon || busy ? <span className="ui-tile-icon">{busy ? <Spinner /> : icon}</span> : null}
      <span className="ui-tile-text">
        <span className="ui-tile-title" id={`${id}-t`}>{title}</span>
        {on === undefined ? null : (
          <span className="ui-tile-switch" aria-hidden="true">
            <span className="ui-tile-switch-track"><span className="ui-tile-switch-knob" /></span>
            {on ? "On" : "Off"}
          </span>
        )}
        <span className={`ui-tile-says${tone ? ` ui-tile-says-${tone}` : ""}`} id={`${id}-s`} aria-live="polite">{says}</span>
      </span>
    </button>
  );
}

/** A row of action tiles: equal widths and equal heights on a wide screen,
 *  one a line at full width on a phone. */
export function ActionTiles({ children, label }: { children: ReactNode; label?: string }) {
  return <div className="ui-tiles" role="group" aria-label={label}>{children}</div>;
}
